import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { enUS, fr } from "date-fns/locale";
import { format as formatFn, isValid, parseISO } from "date-fns";
import { PhoneNumberUtil } from "google-libphonenumber";

export function isset(value: any) {
  return value != "null" && value != null && value != "" && value != undefined;
}

/**
 * Combines multiple class names into a single string.
 * Uses clsx for conditional class application and tailwind-merge to handle Tailwind CSS conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function parsePhoneNumber(number: string): {
  code: string;
  value: string;
} {
  const phoneUtil = PhoneNumberUtil.getInstance();

  try {
    // Parse the number; null region works if number includes '+'
    const parsed = phoneUtil.parse(number, undefined);
    const countryCode = parsed.getCountryCode(); // e.g., 1, 33, 237
    const nationalNumber = parsed.getNationalNumber(); // numeric part

    return {
      code: `+${countryCode}`,
      value: nationalNumber?.toString() as string,
    };
  } catch (e) {
    return { code: "", value: "" };
  }
}

/**
 * Formats a price in FCFA currency format
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "XAF",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function formatDate(
  date: string | Date | null | undefined,
  formatStr: string = "d MMMM yyyy",
  locale: "fr" | "en" = "en"
): string {
  if (!date) return "";

  // 1. Dictionnaire des locales encapsulé
  const localesMap = { fr, en: enUS };

  // 2. Conversion sécurisée en objet Date
  // parseISO est essentiel pour les chaînes provenant d'une API
  const parsedDate = typeof date === "string" ? parseISO(date) : date;

  // 3. Validation pour éviter les crashs si la string est malformée
  if (!isValid(parsedDate)) {
    return "";
  }

  // 4. Formatage final (On force le cast en Date car isValid garantit la validité)
  return formatFn(parsedDate as Date, formatStr, {
    locale: localesMap[locale],
  });
}

export function formatNumber(
  value: number,
  locale = "fr-FR",
  currency?: string
): string {
  if (isNaN(value)) return "0";

  return new Intl.NumberFormat(locale, {
    style: currency ? "currency" : "decimal",
    currency: currency || undefined,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

export function pick(obj: any, keys: string | string[]) {
  if (Array.isArray(keys)) {
    const result: any = {};
    for (const key of keys) {
      if (key in obj) {
        result[key] = obj[key];
      }
    }
    return result;
  } else {
    return obj[keys];
  }
}

export function unPick(obj: any, keys: string | string[]) {
  const keysToRemove = Array.isArray(keys) ? keys : [keys];
  const result: any = {};

  for (const key in obj) {
    if (!keysToRemove.includes(key)) {
      result[key] = obj[key];
    }
  }

  return result;
}

export const addNoneOption = (label: string, options: any) => {
  return [{ label: label, value: "" }, ...options];
};

export function generateRandomString(length = 8) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    result += chars[randomIndex];
  }

  return result;
}

// Modern colors array
export const colors = [
  "#F3C6B8", // Millennial Pink
  "#B7F0D4", // Neo Mint
  "#6B5B95", // Ultra Violet
  "#FF6F61", // Coral Reef
  "#FF5E3A", // Sunset Orange
  "#7FDBFF", // Aqua Sky
  "#9CAF88", // Sage Green
  "#36454F", // Charcoal Gray
  "#DCAE96", // Dusty Rose
  "#007FFF", // Electric Blue
  "#FFE5B4", // Peach Fuzz
  "#6B8E23", // Olive Drab
  "#E6E6FA", // Lavender Mist
  "#E2725B", // Terracotta
  "#008080", // Teal Tide
  "#4C6A92", // Cobalt Slate
  "#FFF1B5", // Buttercream Yellow
  "#B7410E", // Rust Red
  "#FF00FF", // Fuchsia Glow
  "#837060", // Mocha Latte
];

// Function to get first x colors, modulo 20
export function getFirstColors(x: number) {
  const result = [];
  const modulo = 20; // Wrap every 20 colors
  for (let i = 0; i < x; i++) {
    result.push(colors[i % modulo]);
  }
  return result;
}

export function getPDFColumnStyles({
  data,
  getAttributes,
  doc,
  fontSize = 8,
  padding = 4,
  columns,
}: {
  data: any;
  getAttributes: any;
  doc: any;
  fontSize?: number;
  padding?: number;
  columns: any;
}) {
  doc.setFontSize(fontSize);

  const columnStyles: any = {};
  const headers = columns.map((item: any) => item.header);

  columns.forEach((attr: any, colIndex: any) => {
    let maxWidth = doc.getTextWidth(String(headers[colIndex])) + padding;

    data.forEach((instance: any) => {
      const val = String(getAttributes(instance)[attr.name] || "");
      const cellWidth = doc.getTextWidth(val) + padding;
      if (cellWidth > maxWidth) maxWidth = cellWidth;
    });

    const finalWidth = Math.min(Math.max(maxWidth, 15), 60);

    columnStyles[colIndex] = {
      cellWidth: finalWidth,
      halign: attr.name.match(/amount|quantity|refundable|selling_price|price/)
        ? "right"
        : "left",
    };
  });

  return columnStyles;
}

export const generateCSV = ({
  tableName,
  getAttributes,
  data,
  columns,
}: {
  tableName: string;
  getAttributes: any;
  data: any;
  columns: any[];
}) => {
  // 1. Préparation des entêtes (Headers)
  const headers = columns.map((col: any) => col.header).join(",");

  // 2. Extraction des données selon les attributs sélectionnés
  const rows = data.map((instance: any) => {
    return columns
      .map((col: any) => {
        const value = getAttributes(instance)[col.name] ?? "";
        // Nettoyage pour éviter de casser le format CSV (guillemets et virgules)
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      })
      .join(",");
  });

  // 3. Assemblage du contenu
  const csvContent = [headers, ...rows].join("\n");

  // 4. Création et téléchargement du fichier
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  const timestamp = new Date().getTime();
  link.setAttribute("href", url);
  link.setAttribute("download", `${tableName}_export_${timestamp}.csv`);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function generateBatchCode(sequence: any = 0) {
  const seqString = (sequence + 1).toString().padStart(2, "0");
  const timestamp = Date.now().toString(36).toUpperCase(); // temps unique
  const random = Math.random().toString(36).substring(2, 6).toUpperCase(); // anti-collision

  return `${timestamp}-${random}-${seqString}`;
}

export function generateSKU() {
  const timestamp = Date.now().toString(36).toUpperCase(); // temps unique
  const random = Math.random().toString(36).substring(2, 6).toUpperCase(); // anti-collision

  return `${timestamp}-${random}`;
}
